import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import {
  Bell, Clock, Users, Shield, Play, Plus, Trash2, RefreshCw,
  AlertTriangle, AlertCircle, Info, CheckCircle2, Mail, Zap,
  Settings, TrendingDown, DollarSign, CreditCard, BarChart3, Lock
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SEV_CONFIG = {
  critical: { color: 'bg-red-500', text: 'text-red-600', bg: 'bg-red-50', icon: AlertTriangle },
  warning: { color: 'bg-amber-500', text: 'text-amber-600', bg: 'bg-amber-50', icon: AlertCircle },
  info: { color: 'bg-blue-500', text: 'text-blue-600', bg: 'bg-blue-50', icon: Info },
};

const METRICS = [
  { value: 'total_leads', label: 'Total Leads' },
  { value: 'new_leads_week', label: 'New Leads (7d)' },
  { value: 'total_revenue', label: 'Total Revenue' },
  { value: 'open_tickets', label: 'Open Tickets' },
  { value: 'overdue_tasks', label: 'Overdue Tasks' },
  { value: 'active_campaigns', label: 'Active Campaigns' },
  { value: 'loyalty_members', label: 'Loyalty Members' },
  { value: 'remaining', label: 'AI Credits Remaining' },
  { value: 'conversion_rate', label: 'Conversion Rate %' },
  { value: 'avg_resolution_hours', label: 'Avg Resolution Time (h)' },
];

const OPERATORS = [
  { value: 'gt', label: 'Greater than (>)' },
  { value: 'lt', label: 'Less than (<)' },
  { value: 'gte', label: 'Greater or equal (>=)' },
  { value: 'lte', label: 'Less or equal (<=)' },
  { value: 'eq', label: 'Equals (=)' },
];

// ─── Addon Gate Component ────────────────────────────────────────────────────

function AddonGate({ access }) {
  const [activating, setActivating] = useState(false);

  const activate = async () => {
    setActivating(true);
    try {
      await axios.post(`${API}/api/alert-management/addon`, { activate: true });
      toast.success('Alert Management activated!');
      window.location.reload();
    } catch {
      toast.error('Failed to activate add-on');
    } finally {
      setActivating(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[60vh]" data-testid="addon-gate">
      <Card className="max-w-md w-full bg-white border-gray-border">
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-50 to-emerald-50 flex items-center justify-center mx-auto mb-5">
            <Lock className="h-8 w-8 text-teal-600" />
          </div>
          <h2 className="text-xl font-heading font-bold text-text-primary mb-2">Alert Management</h2>
          <p className="text-sm text-gray-medium mb-1">Automated AI monitoring, custom rules, and email digests</p>
          <Badge className="text-xs bg-teal-50 text-teal-700 mb-6">Module Add-on</Badge>
          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left space-y-2">
            <div className="flex items-center gap-2 text-sm text-text-primary"><CheckCircle2 className="h-4 w-4 text-teal-600 flex-shrink-0" />Scheduled AI analysis (daily/weekly)</div>
            <div className="flex items-center gap-2 text-sm text-text-primary"><CheckCircle2 className="h-4 w-4 text-teal-600 flex-shrink-0" />Custom threshold-based alert rules</div>
            <div className="flex items-center gap-2 text-sm text-text-primary"><CheckCircle2 className="h-4 w-4 text-teal-600 flex-shrink-0" />Multi-recipient email digests</div>
            <div className="flex items-center gap-2 text-sm text-text-primary"><CheckCircle2 className="h-4 w-4 text-teal-600 flex-shrink-0" />Full alert history and analytics</div>
          </div>
          <p className="text-sm text-gray-medium mb-4">
            Current plan: <strong className="text-text-primary capitalize">{access.plan}</strong>
            {access.plan === 'enterprise' ? ' (included)' : ` — Add-on $${access.addon_price}/mo`}
          </p>
          {access.plan !== 'enterprise' && (
            <Button className="w-full bg-teal-600 hover:bg-teal-700" onClick={activate} disabled={activating} data-testid="activate-addon-btn">
              {activating ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
              Activate Add-on
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Schedule Panel ──────────────────────────────────────────────────────────

function SchedulePanel() {
  const [config, setConfig] = useState(null);
  const [tenantTz, setTenantTz] = useState('UTC');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/alert-management/schedule`),
      axios.get(`${API}/api/settings/global`).catch(() => ({ data: {} })),
    ]).then(([sched, gs]) => {
      setConfig(sched.data);
      setTenantTz(gs.data?.timezone || 'UTC');
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/api/alert-management/schedule`, config);
      setConfig(data);
      toast.success('Schedule updated');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading || !config) return <div className="flex justify-center p-8"><RefreshCw className="h-5 w-5 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-4" data-testid="schedule-panel">
      <Card className="bg-white border-gray-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-heading flex items-center gap-2"><Clock className="h-4 w-4 text-teal-600" />Analysis Schedule</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-text-primary">Automated Analysis</p>
              <p className="text-xs text-gray-medium">Run AI analysis on your platform metrics automatically</p>
            </div>
            <button
              onClick={() => setConfig(c => ({ ...c, enabled: !c.enabled }))}
              className="focus:outline-none"
              data-testid="toggle-schedule"
            >
              <div className={cn("w-10 h-5 rounded-full transition-colors flex items-center px-0.5", config.enabled ? "bg-teal-500 justify-end" : "bg-gray-300 justify-start")}>
                <div className="w-4 h-4 rounded-full bg-white shadow-sm" />
              </div>
            </button>
          </div>
          {config.enabled && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Frequency</Label>
                  <Select value={config.frequency} onValueChange={v => setConfig(c => ({ ...c, frequency: v }))}>
                    <SelectTrigger data-testid="schedule-frequency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="biweekly">Bi-weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Time ({tenantTz.replace(/_/g, ' ')})</Label>
                  <Input
                    type="time"
                    value={config.time_utc || '09:00'}
                    onChange={e => setConfig(c => ({ ...c, time_utc: e.target.value }))}
                    data-testid="schedule-time"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">Aligned to your Global Settings timezone</p>
                </div>
                {config.frequency === 'weekly' && (
                  <div>
                    <Label className="text-xs">Day of Week</Label>
                    <Select value={String(config.day_of_week ?? 0)} onValueChange={v => setConfig(c => ({ ...c, day_of_week: Number(v) }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map((d, i) => (
                          <SelectItem key={i} value={String(i)}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              {config.last_run && (
                <p className="text-xs text-gray-medium">Last run: {new Date(config.last_run).toLocaleString()}</p>
              )}
              {config.next_run && (
                <p className="text-xs text-teal-600 font-medium">Next run: {new Date(config.next_run).toLocaleString()}</p>
              )}
            </>
          )}
          <Button onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-schedule-btn">
            {saving ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Settings className="h-4 w-4 mr-1" />}
            Save Schedule
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Rules Panel ─────────────────────────────────────────────────────────────

function RulesPanel() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', metric: 'open_tickets', operator: 'gt', threshold: 10, severity: 'warning', enabled: true });

  const fetch = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/alert-management/rules`);
      setRules(data.rules || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const create = async () => {
    if (!form.name.trim()) { toast.error('Rule name required'); return; }
    try {
      await axios.post(`${API}/api/alert-management/rules`, form);
      toast.success('Rule created'); setShowCreate(false);
      setForm({ name: '', metric: 'open_tickets', operator: 'gt', threshold: 10, severity: 'warning', enabled: true });
      fetch();
    } catch { toast.error('Failed to create'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this rule?')) return;
    try { await axios.delete(`${API}/api/alert-management/rules/${id}`); toast.success('Deleted'); fetch(); }
    catch { toast.error('Failed'); }
  };

  const toggleRule = async (rule) => {
    try {
      await axios.put(`${API}/api/alert-management/rules/${rule.id}`, { ...rule, enabled: !rule.enabled });
      fetch();
    } catch { toast.error('Failed to toggle'); }
  };

  if (loading) return <div className="flex justify-center p-8"><RefreshCw className="h-5 w-5 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-4" data-testid="rules-panel">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-medium">{rules.length} custom rule(s)</p>
        <Button onClick={() => setShowCreate(true)} size="sm" data-testid="add-rule-btn"><Plus className="h-4 w-4 mr-1" />Add Rule</Button>
      </div>

      {rules.length === 0 ? (
        <Card className="bg-white border-gray-border"><CardContent className="p-8 text-center">
          <Shield className="h-10 w-10 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-medium text-sm">No custom rules yet</p>
          <p className="text-xs text-gray-400">Create rules to get alerted when metrics cross thresholds</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {rules.map(rule => {
            const sev = SEV_CONFIG[rule.severity] || SEV_CONFIG.info;
            const SevIcon = sev.icon;
            return (
              <Card key={rule.id} className={cn("bg-white border-gray-border", !rule.enabled && "opacity-50")} data-testid={`rule-${rule.id}`}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0", sev.bg)}>
                    <SevIcon className={cn("h-4 w-4", sev.text)} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary truncate">{rule.name}</p>
                    <p className="text-xs text-gray-medium">{rule.metric} {OPERATORS.find(o => o.value === rule.operator)?.label || rule.operator} {rule.threshold}</p>
                  </div>
                  <Badge className={cn("text-[10px]", sev.bg, sev.text)}>{rule.severity}</Badge>
                  <button onClick={() => toggleRule(rule)} className="focus:outline-none">
                    <div className={cn("w-8 h-[18px] rounded-full transition-colors flex items-center px-0.5", rule.enabled ? "bg-teal-500 justify-end" : "bg-gray-300 justify-start")}>
                      <div className="w-3.5 h-3.5 rounded-full bg-white shadow-sm" />
                    </div>
                  </button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => remove(rule.id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md"><DialogHeader><DialogTitle>Create Alert Rule</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Rule Name *</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. High open tickets alert" data-testid="rule-name" /></div>
            <div><Label>Metric</Label>
              <Select value={form.metric} onValueChange={v => setForm(f => ({ ...f, metric: v }))}>
                <SelectTrigger data-testid="rule-metric"><SelectValue /></SelectTrigger>
                <SelectContent>{METRICS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Condition</Label>
                <Select value={form.operator} onValueChange={v => setForm(f => ({ ...f, operator: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{OPERATORS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Threshold</Label><Input type="number" value={form.threshold} onChange={e => setForm(f => ({ ...f, threshold: Number(e.target.value) }))} data-testid="rule-threshold" /></div>
            </div>
            <div><Label>Severity</Label>
              <Select value={form.severity} onValueChange={v => setForm(f => ({ ...f, severity: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="info">Info</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button><Button onClick={create} data-testid="save-rule-btn">Create Rule</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Recipients Panel ────────────────────────────────────────────────────────

function RecipientsPanel() {
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', notify_critical: true, notify_warning: true, notify_info: false });

  const fetch = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/alert-management/recipients`);
      setRecipients(data.recipients || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const add = async () => {
    if (!form.email.trim()) { toast.error('Email required'); return; }
    try {
      await axios.post(`${API}/api/alert-management/recipients`, form);
      toast.success('Recipient added'); setShowAdd(false); setForm({ email: '', name: '', notify_critical: true, notify_warning: true, notify_info: false });
      fetch();
    } catch { toast.error('Failed'); }
  };

  const remove = async (id) => {
    try { await axios.delete(`${API}/api/alert-management/recipients/${id}`); toast.success('Removed'); fetch(); }
    catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex justify-center p-8"><RefreshCw className="h-5 w-5 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-4" data-testid="recipients-panel">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-medium">{recipients.length} recipient(s)</p>
        <Button onClick={() => setShowAdd(true)} size="sm" data-testid="add-recipient-btn"><Plus className="h-4 w-4 mr-1" />Add Recipient</Button>
      </div>

      {recipients.length === 0 ? (
        <Card className="bg-white border-gray-border"><CardContent className="p-8 text-center">
          <Mail className="h-10 w-10 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-medium text-sm">No recipients configured</p>
          <p className="text-xs text-gray-400">Add team members to receive alert digests via email</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {recipients.map(r => (
            <Card key={r.id} className="bg-white border-gray-border" data-testid={`recipient-${r.id}`}>
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0">
                  <Mail className="h-4 w-4 text-teal-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-primary truncate">{r.name || r.email}</p>
                  <p className="text-xs text-gray-medium">{r.email}</p>
                </div>
                <div className="flex gap-1">
                  {r.notify_critical && <Badge className="text-[9px] bg-red-50 text-red-600">Critical</Badge>}
                  {r.notify_warning && <Badge className="text-[9px] bg-amber-50 text-amber-600">Warning</Badge>}
                  {r.notify_info && <Badge className="text-[9px] bg-blue-50 text-blue-600">Info</Badge>}
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => remove(r.id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-sm"><DialogHeader><DialogTitle>Add Recipient</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Email *</Label><Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} data-testid="recipient-email" /></div>
            <div><Label>Name</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
            <div className="space-y-2">
              <Label>Notify for</Label>
              {[
                { key: 'notify_critical', label: 'Critical', color: 'text-red-600' },
                { key: 'notify_warning', label: 'Warning', color: 'text-amber-600' },
                { key: 'notify_info', label: 'Info', color: 'text-blue-600' },
              ].map(opt => (
                <label key={opt.key} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form[opt.key]} onChange={e => setForm(f => ({ ...f, [opt.key]: e.target.checked }))} className="rounded" />
                  <span className={opt.color}>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button><Button onClick={add} data-testid="save-recipient-btn">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── History Panel ───────────────────────────────────────────────────────────

function HistoryPanel() {
  const [history, setHistory] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetch = useCallback(async () => {
    try {
      const params = filter !== 'all' ? { severity: filter } : {};
      const { data } = await axios.get(`${API}/api/alert-management/history`, { params,  });
      setHistory(data.history || []);
      setTotal(data.total || 0);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <div className="flex justify-center p-8"><RefreshCw className="h-5 w-5 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-4" data-testid="history-panel">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-medium">{total} total alert(s)</p>
        <div className="flex gap-1">
          {['all', 'critical', 'warning', 'info'].map(f => (
            <Button key={f} size="sm" variant={filter === f ? 'default' : 'outline'} className="text-xs h-7 px-2 capitalize" onClick={() => setFilter(f)}>{f}</Button>
          ))}
        </div>
      </div>
      {history.length === 0 ? (
        <Card className="bg-white border-gray-border"><CardContent className="p-8 text-center">
          <BarChart3 className="h-10 w-10 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-medium text-sm">No alert history yet</p>
          <p className="text-xs text-gray-400">Alerts will appear here after the first analysis runs</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-1.5">
          {history.map((h, i) => {
            const sev = SEV_CONFIG[h.severity] || SEV_CONFIG.info;
            const SevIcon = sev.icon;
            return (
              <Card key={h.id || i} className="bg-white border-gray-border" data-testid={`history-${i}`}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div className={cn("w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0", sev.bg)}>
                    <SevIcon className={cn("h-3.5 w-3.5", sev.text)} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary truncate">{h.title}</p>
                    <p className="text-xs text-gray-medium truncate">{h.message}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {h.email_sent && <Mail className="h-3.5 w-3.5 text-teal-500" title="Email sent" />}
                    <Badge variant="outline" className="text-[9px]">{h.source === 'custom_rule' ? 'Rule' : 'AI'}</Badge>
                    <span className="text-[10px] text-gray-400 whitespace-nowrap">{h.created_at ? new Date(h.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function AlertManagementPage() {
  const [access, setAccess] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: acc } = await axios.get(`${API}/api/alert-management/access`);
        setAccess(acc);
        if (acc.has_access) {
          const { data: st } = await axios.get(`${API}/api/alert-management/stats`);
          setStats(st);
        }
      } catch { /* silent */ }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/api/alert-management/run-now`, {});
      toast.success(`Analysis complete: ${data.alerts_generated} alerts (${data.ai_alerts} AI + ${data.rule_alerts} rules). ${data.emails_sent} email(s) sent.`);
      // Refresh stats
      const { data: st } = await axios.get(`${API}/api/alert-management/stats`);
      setStats(st);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === 'object' && detail?.code === 'ADDON_REQUIRED') {
        toast.error(detail.message);
      } else {
        toast.error('Failed to run analysis');
      }
    } finally {
      setRunning(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="h-6 w-6 animate-spin text-gray-medium" /></div>;

  if (access && !access.has_access) return <AddonGate access={access} />;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="alert-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">Alert Management<PageInfoTooltip pageKey="alert-management" /></h1>
          <p className="text-gray-medium text-sm mt-1">Automated AI monitoring, custom rules, and email delivery</p>
        </div>
        <Button onClick={runNow} disabled={running} className="bg-teal-600 hover:bg-teal-700" data-testid="run-now-btn">
          {running ? <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> : <Play className="h-4 w-4 mr-1.5" />}
          {running ? 'Analyzing...' : 'Run Analysis Now'}
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Card className="bg-white border-gray-border">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div><p className="text-xs text-gray-medium">Alerts (30d)</p><p className="text-xl font-bold text-text-primary mt-1">{stats.total_alerts_30d}</p></div>
                <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center"><Bell className="h-4 w-4 text-blue-600" /></div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-border">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div><p className="text-xs text-gray-medium">Critical</p><p className="text-xl font-bold text-red-600 mt-1">{stats.by_severity?.critical || 0}</p></div>
                <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center"><AlertTriangle className="h-4 w-4 text-red-500" /></div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-border">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div><p className="text-xs text-gray-medium">Emails Sent</p><p className="text-xl font-bold text-teal-600 mt-1">{stats.emails_sent_30d}</p></div>
                <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center"><Mail className="h-4 w-4 text-teal-600" /></div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-border">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div><p className="text-xs text-gray-medium">Active Rules</p><p className="text-xl font-bold text-text-primary mt-1">{stats.active_rules}</p></div>
                <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center"><Shield className="h-4 w-4 text-purple-600" /></div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-border">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div><p className="text-xs text-gray-medium">Schedule</p><p className="text-sm font-bold text-text-primary mt-1">{stats.schedule_enabled ? 'Active' : 'Off'}</p></div>
                <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", stats.schedule_enabled ? "bg-emerald-50" : "bg-gray-50")}>
                  <Clock className={cn("h-4 w-4", stats.schedule_enabled ? "text-emerald-600" : "text-gray-400")} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="schedule" className="space-y-4">
        <TabsList className="bg-white border border-gray-border w-full sm:w-auto overflow-x-auto flex-nowrap justify-start">
          <TabsTrigger value="schedule" className="text-xs sm:text-sm whitespace-nowrap" data-testid="tab-schedule">Schedule</TabsTrigger>
          <TabsTrigger value="rules" className="text-xs sm:text-sm whitespace-nowrap" data-testid="tab-rules">Custom Rules</TabsTrigger>
          <TabsTrigger value="recipients" className="text-xs sm:text-sm whitespace-nowrap" data-testid="tab-recipients">Recipients</TabsTrigger>
          <TabsTrigger value="history" className="text-xs sm:text-sm whitespace-nowrap" data-testid="tab-history">History</TabsTrigger>
        </TabsList>
        <TabsContent value="schedule"><SchedulePanel /></TabsContent>
        <TabsContent value="rules"><RulesPanel /></TabsContent>
        <TabsContent value="recipients"><RecipientsPanel /></TabsContent>
        <TabsContent value="history"><HistoryPanel /></TabsContent>
      </Tabs>
    </div>
  );
}
